import type { RouteObject } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyOnboarding,
  CompanyOnboardingStart,
  CompanyOnboardingEmployee,
  CompanyOnboardingSettingsPage,
} from "@/utils/lazyLoadComponents";

// ─── Path Segments ────────────────────────────────────────────────────────────
export enum COMPANY_ONBOARDING_PATH_SEGMENTS {
  ROOT = "onboarding",
  START = "start",
  EMPLOYEE = "employee",
  // Module settings (moved out of Company Settings)
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyOnboardingRoutes: RouteObject = {
  path: COMPANY_ONBOARDING_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Onboarding" },
  HydrateFallback: Spinner,
  children: [
    { index: true, Component: CompanyOnboarding },
    {
      path: COMPANY_ONBOARDING_PATH_SEGMENTS.START,
      handle: { breadcrumb: "Start Onboarding" },
      Component: CompanyOnboardingStart,
    },
    {
      path: COMPANY_ONBOARDING_PATH_SEGMENTS.EMPLOYEE,
      handle: { breadcrumb: "Onboarding" },
      Component: CompanyOnboardingEmployee,
    },
    // Module settings — a plain sibling route; nothing else here claims the
    // static "settings" segment.
    {
      path: COMPANY_ONBOARDING_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Onboarding Settings" },
      Component: CompanyOnboardingSettingsPage,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
const _ONBOARDING_ROOT = withCompanyPrefix(
  addSlashes(COMPANY_ONBOARDING_PATH_SEGMENTS.ROOT),
);

// hTab deep-links into the root page's TabPaginationContainer (default
// urlParamKey="hTab"). Unlike Settings' hTab, these stay query params, not
// path segments — so the values here must be kept in sync by hand with the
// TAB enum in onboarding/index.tsx.
const _tab = (hTab: string) => `${_ONBOARDING_ROOT}?hTab=${hTab}`;

export const COMPANY_ONBOARDING_PATHS = Object.freeze({
  ROOT: _ONBOARDING_ROOT,
  TAB_ONBOARDING: _tab("in-onboarding"),
  TAB_INSTANT: _tab("instant-onboarded"),
  TAB_AWAITING_LOGIN: _tab("awaiting-login"),
  START: withCompanyPrefix(
    addSlashes(
      COMPANY_ONBOARDING_PATH_SEGMENTS.ROOT,
      COMPANY_ONBOARDING_PATH_SEGMENTS.START,
    ),
  ),
  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.ONBOARDING.ROOT,
  SETTINGS_VTAB_DIGITAL_SYSTEMS: MODULE_SETTINGS_PATHS.ONBOARDING.VTAB_DIGITAL_SYSTEMS,

  EMPLOYEE: (id: number | string) =>
    `${withCompanyPrefix(
      addSlashes(
        COMPANY_ONBOARDING_PATH_SEGMENTS.ROOT,
        COMPANY_ONBOARDING_PATH_SEGMENTS.EMPLOYEE,
      ),
    )}?id=${id}`,
});
